import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, MessageCircle, Copy } from "lucide-react";
import { Modal, Btn, Field, inputCls } from "./client/ui";
import { BouquetPicker } from "./client/BouquetPicker";

const fmtExp = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const message = (r, name) =>
  `Olá${name ? ` ${name.split(" ")[0]}` : ""}! Seu teste foi liberado.\nUsuário: ${r.username}\nSenha: ${r.password}\nVálido até: ${fmtExp(r.expires_at)}` +
  (r.client?.invoice_link ? `\n\nGostou? Para assinar, é só pagar pelo link:\n${r.client.invoice_link}` : "");

export const TrialButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="quick-trial-button" onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/25">
        <Zap className="h-4 w-4" /> Teste rápido
      </button>
      {open && <TrialDialog onClose={() => setOpen(false)} onCreated={onCreated} />}
    </>
  );
};

const TrialDialog = ({ onClose, onCreated }) => {
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState({ package_id: "", name: "", whatsapp: "", bouquets: [] });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get("/trials/packages").then((r) => {
      const short = [...r.data].sort((a, b) => a.duration - b.duration || a.credits - b.credits)[0];
      setPkgs(r.data); setF((x) => ({ ...x, package_id: short?.id || "" }));
    })
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar pacotes"));
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trials", f);
      setRes(data);
      toast.success("Teste criado no SYSLOG (wetester)");
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao criar teste"); }
    setBusy(false);
  };
  const msg = res ? message(res, f.name) : "";
  const wa = (f.whatsapp || "").replace(/\D/g, "");

  return (
    <Modal testid="trial-dialog" title="Teste rápido · conta wetester" onClose={onClose}>
      {!res ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {pkgs.map((p) => (
              <button key={p.id} data-testid={`trial-pkg-${p.id}`} onClick={() => setF({ ...f, package_id: p.id })}
                className={`rounded-xl border p-2.5 text-left text-xs transition-colors ${f.package_id === p.id ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-[#1F293D] bg-[#111827] text-slate-300 hover:border-[#334155]"}`}>
                <div className="font-semibold">{p.name.replace("[Teste] ", "")}</div>
                <div className="text-slate-500">{p.credits ? `${p.credits} crédito` : "grátis"}</div>
              </button>
            ))}
          </div>
          <Field label="Nome"><input data-testid="trial-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></Field>
          <Field label="WhatsApp"><input data-testid="trial-whatsapp" value={f.whatsapp} placeholder="(11) 99999-9999" onChange={(e) => setF({ ...f, whatsapp: e.target.value })} className={inputCls} /></Field>
          <BouquetPicker prefix="trial" value={f.bouquets} onChange={(b) => setF({ ...f, bouquets: b })} />
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn data-testid="trial-create" disabled={!f.package_id || busy} onClick={create}>{busy ? "Criando..." : "Criar teste"}</Btn>
          </div>
        </div>
      ) : (
        <div data-testid="trial-result" className="space-y-3">
          <pre className="whitespace-pre-wrap rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 font-mono text-sm text-emerald-100">{msg}</pre>
          <div className="flex flex-wrap justify-end gap-2">
            <Btn data-testid="trial-close" tone="ghost" onClick={onClose}>Fechar</Btn>
            <Btn data-testid="trial-copy" tone="ghost" onClick={() => navigator.clipboard.writeText(msg).then(() => toast.success("Copiado"))}>
              <span className="flex items-center gap-1.5"><Copy className="h-4 w-4" /> Copiar</span>
            </Btn>
            <a data-testid="trial-send-wa" href={`https://wa.me/${wa.length <= 11 && wa ? `55${wa}` : wa}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-slate-950 hover:brightness-110">
              <MessageCircle className="h-4 w-4" /> Enviar no WhatsApp
            </a>
          </div>
        </div>
      )}
    </Modal>
  );
};
